/**
 * Slice 53 — Walk and After go room by room when the property has rooms; a property with no rooms
 * keeps the original Walk / After camera. Same props the old steps take, so the job pages only swap the tag.
 */
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { warmPhotos } from "@/lib/offline/queryPersist";
import { useWalkRooms } from "@/hooks/useWalkRooms";
import { GuidedWalkStep } from "@/components/cleaner/GuidedWalkStep";
import { SimpleWalkStep } from "@/components/cleaner/SimpleWalkStep";
import { SimpleBurstPhotoStep } from "@/components/cleaner/SimpleBurstPhotoStep";
import { CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";
import type { RoomPhase } from "@/lib/roomPhotos";

interface Props {
  phase: RoomPhase;
  propertyId: string;
  taskId: string;
  cleanerId?: string | null;
  checklistName?: string | null;
  entryWalkthroughState: unknown;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
  onComplete: () => void;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
}

export function RoomAwareStep({ phase, ...rest }: Props) {
  const { rooms, isLoading } = useWalkRooms(rest.propertyId);

  // Slice 56: keep the cover photos and item photos on the phone for use with no signal.
  useEffect(() => {
    warmPhotos(rooms.flatMap((r) => [r.coverUrl, ...r.items.map((it) => it.photoUrl), ...r.shots.map((sh) => sh.refUrl)]));
  }, [rooms]);

  if (isLoading) {
    return (
      <div className={CLEANER_SIMPLE_STEP_SHELL}>
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#24a89a]" aria-hidden />
        </div>
      </div>
    );
  }

  if (rooms.length > 0) {
    // Before and After both follow the owner's spots: whole room first, then one shot per spot.
    return <GuidedWalkStep rooms={rooms} phase={phase} {...rest} />;
  }

  if (phase === "before") {
    return (
      <SimpleWalkStep
        propertyId={rest.propertyId}
        taskId={rest.taskId}
        cleanerId={rest.cleanerId}
        checklistName={rest.checklistName}
        entryWalkthroughState={rest.entryWalkthroughState}
        includeMaintenance={rest.includeMaintenance}
        includeQuickCheck={rest.includeQuickCheck}
        includeGuestSetup={rest.includeGuestSetup}
        onComplete={rest.onComplete}
        onBack={rest.onBack}
        onSelectSegment={rest.onSelectSegment}
      />
    );
  }
  return (
    <SimpleBurstPhotoStep
      phase="after"
      propertyId={rest.propertyId}
      taskId={rest.taskId}
      entryWalkthroughState={rest.entryWalkthroughState}
      title="After"
      hint="Photo everything clean"
      doneLabel="Next"
      includeMaintenance={rest.includeMaintenance}
      includeQuickCheck={rest.includeQuickCheck}
      includeGuestSetup={rest.includeGuestSetup}
      onComplete={rest.onComplete}
      onBack={rest.onBack}
      onSelectSegment={rest.onSelectSegment}
    />
  );
}
